import React, { useState } from 'react';
import { Loader2, ServerCrash, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiService } from '../../services/api';

/**
 * WorkerConnectModal
 *
 * Hiện khi user chưa có chatbot worker nào được gán (my-chatbots trả về workers=[]).
 * Gọi POST /api/v1/worker/allocate để Orchestrator cấp 1 wkr-chatbot trống.
 * Sau khi thành công → gọi onConnected() để App reload platform list.
 */
interface Props {
  onConnected: () => void;
}

type Phase = 'idle' | 'loading' | 'success' | 'error';

export const WorkerConnectModal: React.FC<Props> = ({ onConnected }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleConnect = async () => {
    setPhase('loading');
    setErrorMsg('');
    try {
      const res = await apiService.requestWorkerAllocation() as any;
      if (res?.success) {
        setPhase('success');
        setTimeout(() => onConnected(), 1200);
      } else {
        throw new Error('Allocation returned non-success');
      }
    } catch (err: any) {
      const errorCode = err?.response?.data?.error;
      const msg =
        errorCode === 'PORTAL_WORKER_OFFLINE'
          ? 'Worker trong portal riêng tư của bạn hiện đang offline. Vui lòng khởi động portal Worker trước rồi thử lại.'
          : err?.response?.data?.message ||
            err?.message ||
            'Không tìm thấy worker trống. Vui lòng thử lại sau.';
      setErrorMsg(msg);
      setPhase('error');
    }
  };

  return (
    /* Full-screen backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#1877F2] to-[#42a5f5]" />

        <div className="px-8 py-8 flex flex-col items-center text-center gap-5">
          {/* Icon */}
          {phase === 'success' ? (
            <div className="w-16 h-16 rounded-2xl bg-green-500 flex items-center justify-center shadow-lg shadow-green-200">
              <CheckCircle2 size={32} className="text-white" />
            </div>
          ) : phase === 'error' ? (
            <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center">
              <AlertTriangle size={32} className="text-red-500" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-[#1877F2] flex items-center justify-center shadow-lg shadow-blue-300">
              <ServerCrash size={30} className="text-white" />
            </div>
          )}

          {/* Title + body */}
          {phase === 'success' ? (
            <>
              <p className="text-[17px] font-bold text-gray-900">Chatbot worker đã sẵn sàng!</p>
              <p className="text-[13px] text-gray-400">Đang tải lại dữ liệu…</p>
            </>
          ) : phase === 'error' ? (
            <>
              <p className="text-[17px] font-bold text-gray-900">Không thể kết nối worker</p>
              <p className="text-[13px] text-gray-500 leading-relaxed">{errorMsg}</p>
              <button
                type="button"
                onClick={handleConnect}
                className="w-full py-3 rounded-xl bg-[#1877F2] text-white font-bold text-[14px] hover:bg-[#166fe5] transition-all shadow-md shadow-blue-200"
              >
                Thử lại
              </button>
            </>
          ) : (
            <>
              <div>
                <p className="text-[17px] font-bold text-gray-900">Chưa có Chatbot Worker</p>
                <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
                  Tài khoản của bạn chưa được gán chatbot worker nào.
                  Nhấn bên dưới để hệ thống tự động cấp một worker còn trống.
                </p>
              </div>
              <button
                type="button"
                disabled={phase === 'loading'}
                onClick={handleConnect}
                className="w-full py-3 rounded-xl bg-[#1877F2] text-white font-bold text-[14px] hover:bg-[#166fe5] disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-md shadow-blue-200 flex items-center justify-center gap-2"
              >
                {phase === 'loading' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Đang kết nối…
                  </>
                ) : (
                  'Kết nối Chatbot Worker'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
